import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAllUsers,
  getUserPlanSummary,
  assignFixedPlan,
  assignPaygPlan,
  updatePlanLimits,
  updateExpiration,
  deleteUserPlan,
  getAllPlans,
  UserListItem,
  UserPlanSummary,
  AssignFixedPlanRequest,
  AssignPaygPlanRequest,
} from "../services/usage.service";
import { UpdatePlanLimitsRequest } from "../types/plan.types";
import { Plan } from "../models/plan";
import { PlanFromAPI, PlanLimits } from "../types/plan.types";

// Query keys
export const adminUsageKeys = {
  all: ['adminUsage'] as const,
  users: () => [...adminUsageKeys.all, 'users'] as const,
  plans: () => [...adminUsageKeys.all, 'plans'] as const,
  userPlan: (userId: string) => [...adminUsageKeys.all, 'userPlan', userId] as const,
} as const;

// Default configuration
const DEFAULT_STALE_TIME = 1 * 60 * 1000; // 1 minute
const DEFAULT_CACHE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to fetch all users (admin only)
 */
export const useAdminUsers = (options?: { enabled?: boolean }) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<UserListItem[], Error>({
    queryKey: adminUsageKeys.users(),
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await getAllUsers(token);

      if (response.error) {
        throw new Error(response.error);
      }

      if (!response.data) {
        throw new Error("No data returned from the server");
      }

      return response.data;
    },
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_CACHE_TIME,
    ...options,
  });
};

/**
 * Hook to fetch all plans from database
 * Backend returns array of plans with flattened limits structure
 */
export const useAdminPlans = (options?: { enabled?: boolean }) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<Plan[], Error>({
    queryKey: adminUsageKeys.plans(),
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await getAllPlans(token);

      if (response.error) {
        throw new Error(response.error);
      }

      if (!response.data) {
        throw new Error("No data returned from the server");
      }

      // Backend returns array directly (not wrapped in object)
      const plansArray: PlanFromAPI[] = Array.isArray(response.data) 
        ? response.data 
        : [];

      if (plansArray.length === 0) {
        throw new Error("No plans returned from the server");
      }

      // Transform backend DTO to frontend model
      // Backend returns plans with limits already flattened
      return plansArray.map((p: PlanFromAPI): Plan => ({
        plan_code: p.plan_code,
        name: p.name,
        plan_kind: p.plan_kind,
        is_custom: p.is_custom,
        cv_limit: p.cv_limit ?? null,
        interview_limit: p.interview_limit ?? null,
        job_limit: p.job_limit ?? null,
        talent_search_limit: p.talent_search_limit ?? null,
        talent_unlock_limit: p.talent_unlock_limit ?? null,
        duration_days: p.duration_days ?? null,
      }));
    },
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_CACHE_TIME,
    ...options,
  });
};

/**
 * Hook to fetch a specific user's plan summary
 */
export const useUserPlanSummary = (userId: string | null, options?: { enabled?: boolean }) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<UserPlanSummary | null, Error>({
    queryKey: adminUsageKeys.userPlan(userId || ''),
    queryFn: async () => {
      if (!userId) return null;

      const token = await getAccessTokenSilently();
      const response = await getUserPlanSummary(token, userId);

      if (response.error) {
        throw new Error(response.error);
      }

      return response.data || null;
    },
    enabled: !!userId && (options?.enabled !== false),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_CACHE_TIME,
    ...options,
  });
};

/**
 * Hook to assign a fixed plan to a user
 */
export const useAssignFixedPlan = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, request }: { userId: string; request: AssignFixedPlanRequest }) => {
      const token = await getAccessTokenSilently();
      const response = await assignFixedPlan(token, userId, request);

      if (response.error) {
        throw new Error(response.error);
      }

      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminUsageKeys.userPlan(variables.userId) });
    },
  });
};

/**
 * Hook to assign a PAYG plan to a user
 */
export const useAssignPaygPlan = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, request }: { userId: string; request: AssignPaygPlanRequest }) => {
      const token = await getAccessTokenSilently();
      const response = await assignPaygPlan(token, userId, request);

      if (response.error) {
        throw new Error(response.error);
      }

      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminUsageKeys.userPlan(variables.userId) });
    },
  });
};

/**
 * Hook to assign an Enterprise plan to a user
 */
export const useAssignEnterprisePlan = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      limits,
      expiresAt,
    }: {
      userId: string;
      limits: PlanLimits;
      expiresAt?: string;
    }) => {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams(
        Object.entries(limits).map(([meter, value]) => [`${meter}_limit`, String(value)])
      );

      if (expiresAt) {
        params.append('expires_at', expiresAt);
      }

      const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;
      const response = await fetch(
        `${apiServerUrl}/api/usage/users/${userId}/assign-enterprise-plan?${params.toString()}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to assign Enterprise plan';
        try {
          const error = await response.json();
          errorMessage = error.detail || error.message || errorMessage;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminUsageKeys.userPlan(variables.userId) });
    },
  });
};

/**
 * Hook to update plan limits/expiration for a user's tenant plan
 */
export const useUpdatePlanLimits = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, request }: { userId: string; request: UpdatePlanLimitsRequest }) => {
      const token = await getAccessTokenSilently();
      const response = await updatePlanLimits(token, userId, request);

      if (response.error) {
        throw new Error(response.error);
      }

      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminUsageKeys.userPlan(variables.userId) });
    },
  });
};

/**
 * Hook to update only the expiration date on a tenant plan
 */
export const useUpdateExpiration = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, expiresAt }: { userId: string; expiresAt: string }) => {
      const token = await getAccessTokenSilently();
      const response = await updateExpiration(token, userId, expiresAt);

      if (response.error) {
        throw new Error(response.error);
      }

      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminUsageKeys.userPlan(variables.userId) });
    },
  });
};

/**
 * Hook to delete a user's plan
 */
export const useDeleteUserPlan = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const token = await getAccessTokenSilently();
      const response = await deleteUserPlan(token, userId);

      if (response.error) {
        throw new Error(response.error);
      }

      return true;
    },
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: adminUsageKeys.userPlan(userId) });
    },
  });
};

